import React, { FunctionComponent, useState } from 'react';
import axios from 'axios';
import { AccountRequestDTO } from '../../types';
import { useAuth } from '../../context/AuthContext';
import './AccountRequestForm.scss';
import Icon from '@mdi/react';
import { mdiPlus } from '@mdi/js';
import LoadingIndicator from '../LoadingIndicator/LoadingIndicator';

interface IProps {
  handleCloseModal(): void;
  refreshData(): void;
}

const AccountRequestForm: FunctionComponent<IProps> = props => {
  const { accessToken } = useAuth();

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});

  const [formData, setFormData] = useState<AccountRequestDTO>({
    type: 'email', // Default contact type to email
    accountType: 'individual' // Default account type to individual
  } as AccountRequestDTO);

  // Validation functions
  const formatPhoneNumber = (value: string, previousValue: string = ''): string => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    const prevDigits = previousValue.replace(/\D/g, '');

    // Limit to 10 digits
    const limitedDigits = digits.substring(0, 10);

    // Check if user is deleting (fewer digits than before)
    const isDeleting = limitedDigits.length < prevDigits.length;

    // If deleting and we're at a formatting boundary, be more conservative
    if (isDeleting) {
      if (limitedDigits.length <= 3) {
        return limitedDigits;
      } else if (limitedDigits.length <= 6) {
        return `(${limitedDigits.substring(0, 3)}) ${limitedDigits.substring(3)}`;
      } else {
        return `(${limitedDigits.substring(0, 3)}) ${limitedDigits.substring(3, 6)}-${limitedDigits.substring(6)}`;
      }
    }

    // Normal progressive formatting for typing
    if (limitedDigits.length >= 6) {
      return `(${limitedDigits.substring(0, 3)}) ${limitedDigits.substring(3, 6)}-${limitedDigits.substring(6)}`;
    } else if (limitedDigits.length >= 3) {
      return `(${limitedDigits.substring(0, 3)}) ${limitedDigits.substring(3)}`;
    } else if (limitedDigits.length > 0) {
      return limitedDigits;
    }
    return '';
  };

  const formatAccountNumber = (value: string): string => {
    // Only allow digits, no length restriction
    return value.replace(/\D/g, '');
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateField = (name: string, value: string, accountType?: string): string => {
    const currentAccountType = accountType || formData.accountType;
    switch (name) {
      case 'firstName':
        if (currentAccountType === 'individual' && !value.trim()) return 'First name is required';
        if (value && value.length > 50) return 'First name must be 50 characters or less';
        return '';
      case 'lastName':
        if (currentAccountType === 'individual' && !value.trim()) return 'Last name is required';
        if (value && value.length > 50) return 'Last name must be 50 characters or less';
        return '';
      case 'businessName':
        if (currentAccountType === 'business' && !value.trim()) return 'Business name is required';
        if (value && value.length > 100) return 'Business name must be 100 characters or less';
        return '';
      case 'accountNumber':
        // No validation needed - formatting function already ensures numbers only
        return '';
      case 'toEmail':
        if (!value.trim()) return 'Email is required';
        if (value.length > 100) return 'Email must be 100 characters or less';
        if (!validateEmail(value)) return 'Please enter a valid email address';
        return '';
      case 'toPhone':
        if (value.trim()) {
          const digits = value.replace(/\D/g, '');
          if (digits.length !== 10) return 'Phone number must be exactly 10 digits';
        }
        return '';
      default:
        return '';
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    let formattedValue = value;
    
    // Apply formatting based on field type
    if (name === 'toPhone') {
      formattedValue = formatPhoneNumber(value, formData.toPhone || '');
    } else if (name === 'accountNumber') {
      formattedValue = formatAccountNumber(value);
    } else if (name === 'firstName' || name === 'lastName') {
      // Limit name fields to 50 characters
      formattedValue = value.substring(0, 50);
    } else if (name === 'businessName') {
      // Limit business name to 100 characters
      formattedValue = value.substring(0, 100);
    } else if (name === 'toEmail') {
      // Limit email to 100 characters
      formattedValue = value.substring(0, 100);
    } else if (name === 'accountType') {
      // Clear business name when switching to individual
      if (value === 'individual') {
        setFormData({
          ...formData,
          [name]: formattedValue,
          businessName: '',
        });
        // Clear business name error if exists
        if (errors.businessName) {
          setErrors({
            ...errors,
            businessName: '',
          });
        }
        return;
      } else if (value === 'business') {
        setFormData({
          ...formData,
          [name]: formattedValue,
          firstName: '',      // Clear individual fields
          lastName: '',       // Clear individual fields
        });
        // Clear firstName/lastName errors if they exist
        if (errors.firstName || errors.lastName) {
          setErrors({
            ...errors,
            firstName: '',
            lastName: '',
          });
        }
        return;
      }
    }

    // Update form data
    setFormData({
      ...formData,
      [name]: formattedValue,
    });
    
    // Clear error for this field if it exists
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: '',
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate all fields
    const newErrors: {[key: string]: string} = {};
    const fieldsToValidate = formData.accountType === 'business'
      ? ['businessName', 'toEmail']
      : ['firstName', 'lastName', 'toEmail'];

    // Add toPhone to validation if it has a value
    if (formData.toPhone && formData.toPhone.trim()) {
      fieldsToValidate.push('toPhone');
    }

    fieldsToValidate.forEach(field => {
      const error = validateField(field, formData[field as keyof AccountRequestDTO] as string || '');
      if (error) {
        newErrors[field] = error;
      }
    });
    
    // If there are validation errors, show them and don't submit
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    // Prepare data for submission - default account number to "new" if blank
    const submissionData = {
      ...formData,
      accountNumber: formData.accountNumber && formData.accountNumber.trim() !== '' 
        ? formData.accountNumber 
        : 'new'
    };
    
    setIsSubmitting(true);
    console.log('Frontend - Submitting account request data:', submissionData);
    try {
      const response = await axios.post<AccountRequestDTO>(`${process.env.REACT_APP_SERVICE_BASEPATH}/account-requests/`, submissionData, {
        headers: {
          'Authorization': `Bearer ${accessToken}`, // Adding the Authorization header
          // Any other headers you need can go here
        },
      });
      console.log('Account Request Created:', response.data);
      props.refreshData();
      props.handleCloseModal();
      // Optionally, clear form or provide user feedback
    } catch (error) {
      console.error('Error creating account request:', error);
      // Handle error
    } finally {
      setIsSubmitting(false); // Reset submission state regardless of outcome
    }
  };

  return (
    <div className='account-request-form'>
      <h2>New Account Request</h2>
      <form onSubmit={handleSubmit}>
        <div className='account-type-section'>
          <div className='form-item'>
            <label>Account Type <span className="required">*</span></label>
            <div className="radio-group">
              <label className="radio-label">
                <input
                  type="radio"
                  name="accountType"
                  value="individual"
                  checked={formData.accountType === 'individual'}
                  onChange={handleChange}
                />
                <span>Individual</span>
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  name="accountType"
                  value="business"
                  checked={formData.accountType === 'business'}
                  onChange={handleChange}
                />
                <span>Business</span>
              </label>
            </div>
          </div>
        </div>
        <div className='form-item-wrapper'>
          {formData.accountType === 'business' && (
            <div className='form-item'>
              <label htmlFor="businessName">Business Name <span className="required">*</span></label>
              <input
                id="businessName"
                name="businessName"
                type="text"
                value={formData.businessName || ''}
                onChange={handleChange}
                maxLength={100}
                required={formData.accountType === 'business'}
                className={errors.businessName ? 'error' : ''}
              />
              {errors.businessName && <span className="error-message">{errors.businessName}</span>}
            </div>
          )}
          {formData.accountType === 'individual' && (
            <>
              <div className='form-item'>
                <label htmlFor="firstName">First Name <span className="required">*</span></label>
            <input 
              id="firstName" 
              name="firstName" 
              type="text" 
              value={formData.firstName || ''} 
              onChange={handleChange}
              maxLength={50}
              required
              className={errors.firstName ? 'error' : ''}
            />
            {errors.firstName && <span className="error-message">{errors.firstName}</span>}
          </div>
          <div className='form-item'>
            <label htmlFor="lastName">Last Name <span className="required">*</span></label>
            <input 
              id="lastName" 
              name="lastName" 
              type="text" 
              value={formData.lastName || ''} 
              onChange={handleChange}
              maxLength={50}
              required
              className={errors.lastName ? 'error' : ''}
            />
            {errors.lastName && <span className="error-message">{errors.lastName}</span>}
          </div>
            </>
          )}
          <div className='form-item'>
            <label htmlFor="accountNumber">CFR Account Number</label>
            <input 
              id="accountNumber" 
              name="accountNumber" 
              type="text" 
              value={formData.accountNumber || ''} 
              onChange={handleChange}
              placeholder="123456789"
              className={errors.accountNumber ? 'error' : ''}
            />
            {errors.accountNumber && <span className="error-message">{errors.accountNumber}</span>}
          </div>
          <div className='form-item'>
            <label htmlFor="toEmail">To Email <span className="required">*</span></label>
            <input 
              id="toEmail" 
              name="toEmail" 
              type="email" 
              value={formData.toEmail || ''} 
              onChange={handleChange}
              placeholder="john.doe@example.com"
              maxLength={100}
              required
              className={errors.toEmail ? 'error' : ''}
            />
            {errors.toEmail && <span className="error-message">{errors.toEmail}</span>}
          </div>
          <div className='form-item'>
            <label htmlFor="toPhone">To Phone</label>
            <input 
              id="toPhone" 
              name="toPhone" 
              type="tel" 
              value={formData.toPhone || ''} 
              onChange={handleChange}
              placeholder="(555) 123-4567"
              maxLength={14}
              className={errors.toPhone ? 'error' : ''}
            />
            {errors.toPhone && <span className="error-message">{errors.toPhone}</span>}
          </div>
          <div className='form-item'>
        <label htmlFor="type">Initial Contact Type</label>
        <select id="type" name="type" value={formData.type} onChange={handleChange}>
          <option value="email">email</option>
        </select>
      </div>
          <div className='form-item'>
            <label htmlFor="note">Note</label>
            <textarea id="note" name="note" value={formData.note} onChange={handleChange} />
          </div>

        </div>
        <button className='submit-button icon' type="submit" disabled={isSubmitting}>Create Account Request <Icon path={mdiPlus} size={1} /></button>
      </form>
      {isSubmitting && <LoadingIndicator />}
    </div>
  );
};

export default AccountRequestForm;
